import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'

/** Any string code from the DB catalog (plus unknown/legacy). */
export type UserAvailabilityStatus = string

export type UserAvailabilityDefinition = {
  code: string
  label: string
  color: string
  durationMinutes: number | null
  inHeaderMenu: boolean
  inAgentStatusOptions: boolean
  pausesSla?: boolean
  routable?: boolean
  isLegacy?: boolean
  sortOrder?: number
}

export const AVAILABILITY_STATUSES_QUERY_KEY = ['user-availability-statuses'] as const

const FALLBACK_COLOR = '#6B7280'

/** Minimal offline fallback when API is unavailable. */
export const OFFLINE_FALLBACK: UserAvailabilityDefinition = {
  code: 'offline',
  label: 'Offline',
  color: FALLBACK_COLOR,
  durationMinutes: null,
  inHeaderMenu: false,
  inAgentStatusOptions: true,
}

const READY_FALLBACK: UserAvailabilityDefinition = {
  code: 'online',
  label: 'Ready',
  color: '#22C55E',
  durationMinutes: null,
  inHeaderMenu: true,
  inAgentStatusOptions: true,
  pausesSla: false,
  routable: true,
}

type ApiStatusRow = {
  id?: number
  code: string
  label: string
  color?: string | null
  duration_minutes?: number | null
  show_in_header?: boolean
  show_in_agent_status?: boolean
  pauses_sla?: boolean
  routable?: boolean
  is_legacy?: boolean
  sort_order?: number
  is_active?: boolean
}

function mapApiRow(row: ApiStatusRow): UserAvailabilityDefinition {
  return {
    code: row.code,
    label: row.label || row.code,
    color: row.color || FALLBACK_COLOR,
    durationMinutes: row.duration_minutes ?? null,
    inHeaderMenu: row.show_in_header === true && row.is_legacy !== true,
    inAgentStatusOptions: row.show_in_agent_status === true && row.is_legacy !== true,
    pausesSla: row.pauses_sla,
    routable: row.routable,
    isLegacy: row.is_legacy,
    sortOrder: row.sort_order,
  }
}

export async function fetchAvailabilityStatuses(): Promise<UserAvailabilityDefinition[]> {
  const res = await apiClient.get('/user/availability-statuses')
  const body = res.data
  const rows: ApiStatusRow[] = Array.isArray(body?.data)
    ? body.data
    : Array.isArray(body)
      ? body
      : []
  return rows.map(mapApiRow)
}

/** Static fallback list used before first fetch / on error. */
export function fallbackAvailabilityDefinitions(): UserAvailabilityDefinition[] {
  return [READY_FALLBACK, OFFLINE_FALLBACK]
}

export function useAvailabilityCatalog() {
  const query = useQuery({
    queryKey: AVAILABILITY_STATUSES_QUERY_KEY,
    queryFn: fetchAvailabilityStatuses,
    staleTime: 60_000,
    retry: 1,
  })

  const definitions = useMemo(() => {
    if (query.data && query.data.length > 0) return query.data
    return fallbackAvailabilityDefinitions()
  }, [query.data])

  const byCode = useMemo(() => {
    const map = new Map<string, UserAvailabilityDefinition>()
    for (const d of definitions) {
      map.set(d.code, d)
    }
    // Ensure offline always resolvable for display
    if (!map.has('offline')) map.set('offline', OFFLINE_FALLBACK)
    return map
  }, [definitions])

  const headerOptions = useMemo(
    () => definitions.filter((d) => d.inHeaderMenu).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [definitions]
  )

  const agentStatusOptions = useMemo(
    () =>
      definitions
        .filter((d) => d.inAgentStatusOptions)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((d) => ({ value: d.code, label: d.label })),
    [definitions]
  )

  return {
    ...query,
    definitions,
    byCode,
    headerOptions,
    agentStatusOptions,
  }
}

export function availabilityDefinitionFromMap(
  byCode: Map<string, UserAvailabilityDefinition>,
  status: string | null | undefined
): UserAvailabilityDefinition {
  const code = (status || 'offline').toLowerCase()
  return byCode.get(code) ?? {
    code,
    label: code.charAt(0).toUpperCase() + code.slice(1).replace(/_/g, ' '),
    color: FALLBACK_COLOR,
    durationMinutes: null,
    inHeaderMenu: false,
    inAgentStatusOptions: false,
  }
}

/** Sync helpers using last-known query cache or fallbacks (for non-hook contexts). */
let cachedByCode: Map<string, UserAvailabilityDefinition> | null = null

export function setAvailabilityCache(definitions: UserAvailabilityDefinition[]): void {
  const map = new Map<string, UserAvailabilityDefinition>()
  for (const d of definitions) map.set(d.code, d)
  if (!map.has('offline')) map.set('offline', OFFLINE_FALLBACK)
  cachedByCode = map
}

export function availabilityDefinition(status: string | null | undefined): UserAvailabilityDefinition {
  const code = (status || 'offline').toLowerCase()
  if (cachedByCode?.has(code)) return cachedByCode.get(code)!
  if (code === 'online') return READY_FALLBACK
  if (code === 'offline') return OFFLINE_FALLBACK
  return {
    code,
    label: code.charAt(0).toUpperCase() + code.slice(1).replace(/_/g, ' '),
    color: FALLBACK_COLOR,
    durationMinutes: null,
    inHeaderMenu: false,
    inAgentStatusOptions: false,
  }
}

export function availabilityStatusColor(status: string | null | undefined): string {
  return availabilityDefinition(status).color
}

export function availabilityStatusLabel(status: string | null | undefined): string {
  return availabilityDefinition(status).label
}

export function availabilityDurationMinutes(status: string | null | undefined): number | null {
  return availabilityDefinition(status).durationMinutes
}

export function isUserAvailabilityStatus(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}
